import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { LuMic, LuMicOff } from 'react-icons/lu';
import './GlobalVoiceInput.css';

/**
 * Safely updates input/textarea value and dispatches synthetic events so
 * React controlled components receive the update immediately.
 */
function setNativeValue(element, value) {
  if (!element) return;
  const prototype = element instanceof HTMLTextAreaElement
    ? window.HTMLTextAreaElement.prototype
    : window.HTMLInputElement.prototype;

  const descriptor = Object.getOwnPropertyDescriptor(prototype, 'value');
  if (descriptor && descriptor.set) {
    descriptor.set.call(element, value);
  } else {
    element.value = value;
  }

  element.dispatchEvent(new Event('input', { bubbles: true }));
  element.dispatchEvent(new Event('change', { bubbles: true }));
}

export function isEligibleElement(el) {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (el.disabled || el.readOnly) return false;
  if (el.dataset.noVoiceOverlay === 'true') return false;

  const tagName = el.tagName?.toLowerCase();
  if (tagName === 'textarea') return true;

  if (tagName === 'input') {
    const type = (el.type || 'text').toLowerCase();
    const allowed = ['text', 'search', 'email', 'url', 'tel'];
    if (!allowed.includes(type)) return false;

    // Never attach voice dictation to password fields (security & prevents collision with show/hide toggles)
    const name = (el.name || '').toLowerCase();
    const id = (el.id || '').toLowerCase();
    const autocomplete = (el.autocomplete || '').toLowerCase();
    if (
      name.includes('password') ||
      id.includes('password') ||
      autocomplete.includes('password') ||
      el.dataset.passwordField === 'true'
    ) {
      return false;
    }

    return true;
  }

  return false;
}

/**
 * Calculates clearance needed to avoid overlapping any visible sibling buttons
 * or action elements on the right side of the input (e.g., clear crosses, toggle buttons).
 */
export function calculateRightOffset(el, rect, btnSize = 18) {
  const baseOffset = 7;
  let maxClearance = baseOffset;

  if (!el.parentElement) {
    return baseOffset;
  }

  const siblings = Array.from(el.parentElement.children);
  for (const sib of siblings) {
    if (sib === el || !(sib instanceof HTMLElement)) continue;

    const style = window.getComputedStyle(sib);
    if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) {
      continue;
    }

    const sibRect = sib.getBoundingClientRect();
    if (sibRect.width <= 0 || sibRect.height <= 0) continue;

    // Sibling must vertically overlap with the input
    const vOverlap = sibRect.top < rect.bottom - 2 && sibRect.bottom > rect.top + 2;
    if (!vOverlap) continue;

    // Sibling must be in the right half of the input (to avoid left-side icons)
    const isInRightHalf = sibRect.left >= rect.left + rect.width * 0.4;
    const overlapsInputRight = sibRect.right >= rect.right - 90 && sibRect.left <= rect.right + 4;

    if (isInRightHalf && overlapsInputRight) {
      const defaultMicLeft = rect.right - btnSize - baseOffset;
      const defaultMicRight = rect.right - baseOffset;

      const collidesWithDefaultMic = sibRect.left < defaultMicRight + 2 && sibRect.right > defaultMicLeft - 2;

      if (collidesWithDefaultMic) {
        const siblingLeftFromRight = rect.right - sibRect.left;
        if (siblingLeftFromRight > 0) {
          const neededOffset = siblingLeftFromRight + 6;
          if (neededOffset > maxClearance) {
            maxClearance = neededOffset;
          }
        }
      }
    }
  }

  return maxClearance;
}

function isElementVisible(el) {
  if (!el || !document.contains(el)) return false;
  const rect = el.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return false;

  // Viewport bounds
  if (
    rect.bottom <= 0 ||
    rect.top >= window.innerHeight ||
    rect.right <= 0 ||
    rect.left >= window.innerWidth
  ) {
    return false;
  }

  const style = window.getComputedStyle(el);
  if (style.display === 'none' || style.visibility === 'hidden' || parseFloat(style.opacity) === 0) {
    return false;
  }

  // Check overflow parents (e.g. scrollable modals, sidebars)
  let parent = el.parentElement;
  while (parent && parent !== document.body && parent !== document.documentElement) {
    const parentStyle = window.getComputedStyle(parent);
    const overflowY = parentStyle.overflowY;
    const overflowX = parentStyle.overflowX;
    if (
      overflowY === 'hidden' || overflowY === 'auto' || overflowY === 'scroll' ||
      overflowX === 'hidden' || overflowX === 'auto' || overflowX === 'scroll'
    ) {
      const parentRect = parent.getBoundingClientRect();
      if (
        rect.bottom < parentRect.top ||
        rect.top > parentRect.bottom ||
        rect.right < parentRect.left ||
        rect.left > parentRect.right
      ) {
        return false;
      }
    }
    parent = parent.parentElement;
  }

  return true;
}

let nextOverlayId = 1;
const overlayIdMap = new WeakMap();

function getOverlayId(el) {
  if (!overlayIdMap.has(el)) {
    overlayIdMap.set(el, `gvi-item-${nextOverlayId++}`);
  }
  return overlayIdMap.get(el);
}

export default function GlobalVoiceInput() {
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [listeningEl, setListeningEl] = useState(null);

  const recognitionRef = useRef(null);
  const baseTextRef = useRef('');
  const listeningElRef = useRef(null);
  const rafRef = useRef(null);

  listeningElRef.current = listeningEl;

  // Scan all eligible inputs on the screen and compute their positions
  const scanAndSync = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      const allElements = Array.from(document.querySelectorAll('input, textarea'));
      const visibleItems = [];

      const btnSize = 18;
      const rightOffset = 7;

      for (const el of allElements) {
        if (!isEligibleElement(el) || !isElementVisible(el)) {
          continue;
        }

        const rect = el.getBoundingClientRect();
        const rightOffset = calculateRightOffset(el, rect, btnSize);

        // Add padding right to input so text doesn't overlap the mic icon or trailing elements
        const neededPadding = rightOffset + btnSize + 8;
        const currentPR = parseInt(window.getComputedStyle(el).paddingRight, 10) || 0;
        if (currentPR < neededPadding) {
          el.style.paddingRight = `${neededPadding}px`;
        }

        const isTextarea = el.tagName?.toLowerCase() === 'textarea';
        const top = isTextarea ? rect.top + 7 : rect.top + (rect.height - btnSize) / 2;
        const left = rect.right - btnSize - rightOffset;
        const id = getOverlayId(el);

        visibleItems.push({
          id,
          top,
          left,
          element: el,
        });
      }

      setItems(visibleItems);

      // If currently listening element was removed or hidden, cancel recognition
      if (listeningElRef.current && !isElementVisible(listeningElRef.current)) {
        if (recognitionRef.current) {
          try {
            recognitionRef.current.stop();
          } catch (_) {}
        }
        setListeningEl(null);
      }
    });
  }, []);

  // Sync on mount, route change, scroll, resize, and user input
  useEffect(() => {
    scanAndSync();

    const handleScrollOrResize = () => scanAndSync();
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    window.addEventListener('input', handleScrollOrResize, true);
    window.addEventListener('change', handleScrollOrResize, true);

    // Mutation observer to detect dynamic additions (modals, new fields)
    const observer = new MutationObserver(() => {
      scanAndSync();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'hidden'],
    });

    // Periodic check to capture smooth transitions/animations
    const interval = setInterval(scanAndSync, 600);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
      window.removeEventListener('input', handleScrollOrResize, true);
      window.removeEventListener('change', handleScrollOrResize, true);
      observer.disconnect();
      clearInterval(interval);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [location, scanAndSync]);

  // Cleanup speech recognition on unmount
  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (_) {}
      }
    };
  }, []);

  const handleToggleVoice = (e, targetEl) => {
    e.preventDefault();
    e.stopPropagation();

    if (!targetEl) return;

    // If currently listening to this element, stop
    if (listeningEl === targetEl) {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (_) {}
      }
      setListeningEl(null);
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.warn('Speech recognition not supported in this browser.');
      return;
    }

    try {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch (_) {}
      }

      const recognition = new SpeechRecognition();
      recognitionRef.current = recognition;

      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = navigator.language || 'en-US';
      recognition.maxAlternatives = 1;

      baseTextRef.current = (targetEl.value || '').trim();

      recognition.onstart = () => {
        setListeningEl(targetEl);
        targetEl.focus();
      };

      recognition.onresult = (event) => {
        let interimText = '';
        let finalText = '';

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          const trans = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalText += trans;
          } else {
            interimText += trans;
          }
        }

        const speechChunk = (finalText || interimText).trim();
        if (speechChunk && targetEl && document.contains(targetEl)) {
          let updatedText = '';
          if (baseTextRef.current) {
            updatedText = `${baseTextRef.current} ${speechChunk}`;
          } else {
            updatedText = speechChunk.charAt(0).toUpperCase() + speechChunk.slice(1);
          }
          setNativeValue(targetEl, updatedText);
        }
      };

      recognition.onerror = (event) => {
        if (event.error !== 'no-speech' && event.error !== 'aborted') {
          console.warn('Global speech recognition error:', event.error);
        }
        setListeningEl(null);
      };

      recognition.onend = () => {
        setListeningEl(null);
      };

      recognition.start();
    } catch (err) {
      console.warn('Could not start speech recognition:', err);
      setListeningEl(null);
    }
  };

  if (items.length === 0) return null;

  return (
    <div className="gvi-root-layer" aria-hidden="false">
      {items.map(({ id, top, left, element }) => {
        const isCurrentlyListening = listeningEl === element;
        return (
          <div
            key={id}
            className="gvi-mic-container"
            style={{
              top: `${top}px`,
              left: `${left}px`,
            }}
          >
            <button
              type="button"
              className={`gvi-mic-btn ${isCurrentlyListening ? 'listening' : ''}`}
              onMouseDown={(e) => {
                // Prevent stealing focus from the input field
                e.preventDefault();
              }}
              onClick={(e) => handleToggleVoice(e, element)}
              title={
                isCurrentlyListening
                  ? 'Listening... Click to stop'
                  : 'Click to dictate (Speech to Text)'
              }
              aria-label={
                isCurrentlyListening
                  ? 'Stop speech recognition'
                  : 'Start speech recognition'
              }
            >
              {isCurrentlyListening ? <LuMicOff size={13} /> : <LuMic size={13} />}
            </button>
          </div>
        );
      })}
    </div>
  );
}
